import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { query, where, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useSchool } from '../../contexts/SchoolContext';
import { useCollection } from '../../hooks/useFirestore';
import { outOfLessonCol } from '../../lib/firestore';
import { OolFoundControls, useOolMarkFound, type OolMarkFound } from '../behaviour/OolFoundControls';
import type { OutOfLesson } from '../../types';

const MAX_SHOWN = 6;

/**
 * Live "out of lesson right now" panel on Home. A pupil missing from a lesson is
 * time-critical — staff shouldn't have to remember to go and look at the
 * Pastoral page to find out, so it surfaces on the first screen after sign-in.
 *
 * Scoped to today: an unresolved record from a previous day is a data-hygiene
 * problem for the end-of-day chase to report, not something to put in front of
 * a teacher as if the pupil were missing now.
 *
 * Any member of staff can mark a pupil found from here, with an optional
 * reason — whoever sees the child first should not have to go to another
 * page to say so. It is the same control and the same one-way `found` the Out
 * Of Lesson page writes (the rules pin who did it), so the pupil leaves the
 * list at once.
 */
export default function HomeOutOfLesson() {
  const { appUser } = useAuth();
  const { activeModules, activePastoralFeatures } = useSchool();
  const schoolId = appUser?.schoolId ?? '';
  const role = appUser?.role;
  const found = useOolMarkFound({ uid: appUser?.id ?? '', name: appUser?.displayName ?? '—' });

  // Staff-only, and only when the school actually runs OOL.
  const visible = !!schoolId
    && !!role && role !== 'superAdmin' && role !== 'student'
    && activeModules.includes('behaviour')
    && activePastoralFeatures.includes('ool');

  // Rules on outOfLesson check resource.data.schoolId, so the schoolId equality
  // is required in the query itself or the listen is denied.
  const { data, error, loading } = useCollection<OutOfLesson>(
    visible ? query(outOfLessonCol, where('schoolId', '==', schoolId), where('status', '==', 'out')) : null,
    `${schoolId}:home-ool`,
  );

  const today = useMemo(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const cutoff = Timestamp.fromDate(midnight).toMillis();
    return data
      .filter((o) => (o.createdAt?.toMillis() ?? 0) >= cutoff)
      .sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0));
  }, [data]);

  if (!visible) return null;

  return (
    <div className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-700">
          Out of lesson{today.length > 0 ? ` (${today.length})` : ''}
        </h2>
        <Link
          to="/behaviour/out-of-lesson"
          className="shrink-0 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-rose-700"
        >
          Create New OOL Alert →
        </Link>
      </div>

      {error ? (
        <p className="card p-4 text-sm text-amber-700">
          Couldn’t load who’s out of lesson — refresh to try again.
        </p>
      ) : loading ? (
        <p className="card p-4 text-sm text-gray-400">Loading…</p>
      ) : today.length === 0 ? (
        <p className="card p-4 text-sm text-gray-400">Nobody is out of lesson today.</p>
      ) : (
        <div className="card divide-y divide-gray-100 p-0">
          {today.slice(0, MAX_SHOWN).map((o) => (
            <OutRow key={o.id} o={o} found={found} />
          ))}
          {today.length > MAX_SHOWN && (
            <Link to="/behaviour/out-of-lesson" className="block px-4 py-2.5 text-xs font-medium text-brand-600 hover:underline">
              View all {today.length} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

/** One pupil who is out, with the same reason list and Found button as the
 *  Out Of Lesson page. */
function OutRow({ o, found }: { readonly o: OutOfLesson & { id: string }; readonly found: OolMarkFound }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
      <span>
        <span className="font-medium text-gray-800">{o.studentName}</span>
        {o.yearGroup ? <span className="text-gray-400"> · Y{o.yearGroup}</span> : null}
        {o.lesson ? <span className="text-gray-400"> · {o.lesson}</span> : null}
        {o.room ? <span className="text-gray-400"> · should be in {o.room}</span> : null}
        <span className="text-gray-400">
          {o.createdAt
            ? ` · ${o.createdAt.toDate().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
            : ''}
          {' · by '}{o.raisedByName}
        </span>
      </span>
      <OolFoundControls id={o.id} studentName={o.studentName} found={found} />
    </div>
  );
}
